import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { registerUserScopedStore } from '@/lib/userScopedPersist';

/**
 * Persistent global timer for time-entry tracking.
 *
 * - `running` is true while the timer is ticking
 * - `startedAt` is the epoch ms when the current run began
 * - `accumulatedMs` is time logged before the current run (so pause+resume works)
 * - `description` and `matterId` are draft fields for the eventual time entry
 *
 * Total elapsed = accumulatedMs + (running ? Date.now() - startedAt : 0)
 */
interface TimerState {
  /** The user this timer belongs to (see userScopedPersist). */
  ownerUserId: string | null;
  running: boolean;
  startedAt: number | null;
  accumulatedMs: number;
  description: string;
  matterId: string;

  start: () => void;
  pause: () => void;
  reset: () => void;
  /** Wipe the timer and re-stamp its owner (cross-user guard). */
  resetForUser: (userId: string | null) => void;
  setDescription: (v: string) => void;
  setMatterId: (v: string) => void;
}

export const useTimerStore = create<TimerState>()(
  persist(
    (set, get) => ({
      ownerUserId: null,
      running: false,
      startedAt: null,
      accumulatedMs: 0,
      description: '',
      matterId: '',

      start: () => {
        if (get().running) return;
        set({ running: true, startedAt: Date.now() });
      },
      pause: () => {
        const { running, startedAt, accumulatedMs } = get();
        if (!running || !startedAt) return;
        set({
          running: false,
          startedAt: null,
          accumulatedMs: accumulatedMs + (Date.now() - startedAt),
        });
      },
      reset: () => set({ running: false, startedAt: null, accumulatedMs: 0, description: '', matterId: '' }),
      resetForUser: (userId) =>
        set({ ownerUserId: userId, running: false, startedAt: null, accumulatedMs: 0, description: '', matterId: '' }),
      setDescription: (v) => set({ description: v }),
      setMatterId: (v) => set({ matterId: v }),
    }),
    // v1: introduce per-user scoping; wipe any pre-scoping timer on upgrade.
    {
      name: 'jurivoxa-timer',
      version: 1,
      migrate: () => ({
        ownerUserId: null, running: false, startedAt: null, accumulatedMs: 0, description: '', matterId: '',
      }),
    },
  ),
);

// Cross-user guard — a running timer + its matter draft must not carry across
// accounts on a shared browser (see userScopedPersist).
registerUserScopedStore({
  getOwner: () => useTimerStore.getState().ownerUserId,
  reset: (uid) => useTimerStore.getState().resetForUser(uid),
});

/** Format milliseconds as HH:MM:SS */
export function formatElapsed(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 1000));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
}

/** Convert milliseconds to decimal hours (e.g. 5400000 → 1.5) */
export function msToHours(ms: number): number {
  return Math.round((ms / 1000 / 3600) * 100) / 100;
}
